import { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import Form from '@rjsf/mui';
import validator from '@rjsf/validator-ajv8';
import type { RJSFSchema, UiSchema } from '@rjsf/utils';
import type { CatalogDataFieldDefinitions } from '@src/types';
import KeyValueEditor from './KeyValueEditor';

interface TypedDataEditorProps {
  value: Record<string, unknown>;
  onChange: (data: Record<string, unknown>) => void;
  definitions?: CatalogDataFieldDefinitions | null;
  label?: string;
}

/**
 * Creates UI schema for better form rendering.
 * Hides JSON Schema meta-fields that shouldn't be editable.
 */
function createUiSchema(): UiSchema {
  return {
    'ui:submitButtonOptions': {
      norender: true, // Hide submit button since we handle saving elsewhere
    },
    // Always hide JSON Schema meta-fields if they appear in data
    type: { 'ui:widget': 'hidden' },
    properties: { 'ui:widget': 'hidden' },
    additionalProperties: { 'ui:widget': 'hidden' },
  };
}

/**
 * Type-aware editor for variant data fields using JSON Schema forms.
 *
 * When `definitions` are provided, automatically generates a form based on
 * the complete JSON Schema from the backend.
 *
 * Falls back to the generic KeyValueEditor when no definitions exist.
 */
const TypedDataEditor = ({
  value,
  onChange,
  definitions,
  label = 'Data',
}: TypedDataEditorProps) => {
  // The backend returns complete JSON Schema, use it directly
  const schema = useMemo((): RJSFSchema | null => {
    return definitions ? (definitions as RJSFSchema) : null;
  }, [definitions]);

  const uiSchema = useMemo(() => createUiSchema(), []);

  // Fall back to generic key-value editor when no schema is available
  if (!schema) {
    return <KeyValueEditor value={value} onChange={onChange} label={label} />;
  }

  return (
    <Box sx={{ mb: 2 }}>
      {label && (
        <Typography variant="subtitle2" gutterBottom>
          {label}
        </Typography>
      )}
      <Form
        schema={schema}
        uiSchema={uiSchema}
        formData={value}
        validator={validator}
        onChange={e => onChange(e.formData)}
        liveValidate={false}
        showErrorList={false}
        omitExtraData={false} // Keep extra fields that aren't in schema
      />
    </Box>
  );
};

export default TypedDataEditor;
